import type { WorkflowNodeJSON } from '@flowgram.ai/free-layout-editor';
import { nanoid } from 'nanoid';
import type { MyIpItem } from '$/domains/myIp/types';
import { getMyIpCreationValue } from '$/domains/myIp/agentSnapshot';
import type { CharacterIPMedia } from '$/components/CharacterIPPanel/types';
import { resolveVideoCoverUrl } from '$/utils/videoCoverUrl';
import { TVFlowNodeNextTypeEnum as NodeType } from '../config/node.config';
import {
  getIpGroupChildLayout,
  layoutIpGroupChildren,
  IP_GROUP_PADDING,
  IP_GROUP_MEDIA_NODE_HEADER_HEIGHT,
  type IpGroupAssetType,
  type IpGroupRole,
} from './ipGroup';
import { getMediaSafeAreaSize } from '../hooks/useMediaSafeArea';

export type IpGroupRoleLabels = Record<IpGroupRole, string>;

const DEFAULT_IP_GROUP_ROLE_LABELS: IpGroupRoleLabels = {
  main: '主图',
  turnaround: '三视图',
  voice: '音色',
  settings: '基础设定',
  material: '更多素材',
};

/** 为 IP 快照成员生成统一的“角色名-素材类型”节点标题。 */
function getIpGroupNodeName(ipName: string, role: IpGroupRole, labels: IpGroupRoleLabels) {
  return `${ipName}-${labels[role]}`;
}

/** Flowgram 横坐标使用节点中心；标准媒体字段保留原图与真实水印地址，供连线、下载和解组后的渲染器消费。 */
export function buildIpGroupMediaNode(
  media: CharacterIPMedia | null | undefined,
  role: IpGroupRole,
  ipNameOrId: string,
  idOrIndex?: string | number,
  index = 0,
  labels: IpGroupRoleLabels = DEFAULT_IP_GROUP_ROLE_LABELS,
): WorkflowNodeJSON {
  const usesLegacyArguments = typeof idOrIndex !== 'string';
  const ipName = usesLegacyArguments ? media?.name || '' : ipNameOrId;
  const id = usesLegacyArguments ? ipNameOrId : String(idOrIndex);
  const materialIndex = usesLegacyArguments && typeof idOrIndex === 'number' ? idOrIndex : index;
  const layout = getIpGroupChildLayout(role, materialIndex);
  let type = NodeType.IMAGE_NODE;
  if (media?.kind === 'video') type = NodeType.VIDEO_NODE;
  if (media?.kind === 'audio' || role === 'voice') type = NodeType.AUDIO_NODE;
  const numericAssetId = media?.assetId && /^\d+$/.test(media.assetId) ? Number(media.assetId) : undefined;
  const assetId = numericAssetId && Number.isSafeInteger(numericAssetId) && numericAssetId > 0 ? numericAssetId : undefined;
  const videoCover = media?.kind === 'video' ? resolveVideoCoverUrl(media.thumbnailUrl, media.url) : undefined;
  const watermarkVideoCover = media?.kind === 'video' && media.watermarkUrl
    ? resolveVideoCoverUrl(media.watermarkCoverUrl, media.watermarkUrl) : undefined;
  return {
    id,
    type,
    meta: { position: { x: layout.x + layout.width / 2, y: layout.y } },
    data: {
      name: getIpGroupNodeName(ipName, role, labels),
      isCustomer: true,
      ipGroupRole: role,
      ipMaterialKey: role === 'material' ? media?.id : undefined,
      media_list: media?.url ? [{
        url: media.url,
        ...(type === NodeType.IMAGE_NODE ? { originUrl: media.url, source: 'ip' } : {}),
        watermark_url: media.watermarkUrl || media.url,
        ...(media?.kind !== 'video' && media.thumbnailUrl ? { cover_url: media.thumbnailUrl } : {}),
        ...(videoCover ? { cover_url: videoCover } : {}),
        ...(watermarkVideoCover ? { watermark_cover_url: watermarkVideoCover } : {}),
        ...(assetId ? { assetId } : {}),
        ...(typeof media.fileSize === 'number' && media.fileSize > 0 ? { size: media.fileSize } : {}),
        ...(typeof media.duration === 'number' && media.duration > 0 ? { duration: media.duration } : {}),
        name: media.name,
        ...(typeof media.width === 'number' && media.width > 0 ? { width: media.width } : {}),
        ...(typeof media.height === 'number' && media.height > 0 ? { height: media.height } : {}),
      }] : [],
    },
  };
}

/** 根据已下发的媒体元数据预估 IP 组占位，避免图片挂载后扩宽组边界覆盖同批节点。 */
export function getIpGroupSnapshotInitialSize(snapshot: WorkflowNodeJSON) {
  const children = snapshot.blocks ?? [];
  const positions = layoutIpGroupChildren(children.map((node) => {
    const role = node.data?.ipGroupRole as IpGroupRole;
    const fallback = getIpGroupChildLayout(role);
    const media = node.data?.media_list?.[0] as { width?: unknown; height?: unknown; } | undefined;
    const width = typeof media?.width === 'number' && media.width > 0 ? media.width : undefined;
    const height = typeof media?.height === 'number' && media.height > 0 ? media.height : undefined;
    if (!width || !height || (role !== 'main' && role !== 'turnaround' && role !== 'material')) {
      return {
        id: node.id,
        role,
        width: fallback.width,
        height: fallback.height,
      };
    }
    const content = getMediaSafeAreaSize({ media: { width, height } });
    return {
      id: node.id,
      role,
      width: content.width,
      height: content.height + IP_GROUP_MEDIA_NODE_HEADER_HEIGHT,
    };
  }));
  const sizes = new Map(children.map((node) => {
    const role = node.data?.ipGroupRole as IpGroupRole;
    const fallback = getIpGroupChildLayout(role);
    const media = node.data?.media_list?.[0] as { width?: unknown; height?: unknown; } | undefined;
    const width = typeof media?.width === 'number' && media.width > 0 ? media.width : undefined;
    const height = typeof media?.height === 'number' && media.height > 0 ? media.height : undefined;
    const content = width && height && (role === 'main' || role === 'turnaround' || role === 'material')
      ? getMediaSafeAreaSize({ media: { width, height } }) : fallback;
    return [node.id, {
      width: content.width,
      height: content === fallback ? fallback.height : content.height + IP_GROUP_MEDIA_NODE_HEADER_HEIGHT,
    }];
  }));
  return positions.reduce((size, position) => {
    const child = sizes.get(position.id)!;
    return {
      width: Math.max(size.width, position.x + child.width / 2 + IP_GROUP_PADDING),
      height: Math.max(size.height, position.y + child.height + IP_GROUP_PADDING),
    };
  }, { width: IP_GROUP_PADDING * 2, height: IP_GROUP_PADDING * 2 });
}

/** 在 IP 组创建前补齐图片自然尺寸，保证批量落点与图片节点加载后的最终宽度一致。 */
export async function hydrateIpGroupSnapshotImageSizes(snapshot: WorkflowNodeJSON): Promise<WorkflowNodeJSON> {
  if (typeof Image === 'undefined') return snapshot;
  const blocks = snapshot.blocks ?? [];
  const sizes = await Promise.all(blocks.map(async (node) => {
    const role = node.data?.ipGroupRole as IpGroupRole | undefined;
    if (role !== 'main' && role !== 'turnaround' && role !== 'material') return undefined;
    const media = node.data?.media_list?.[0] as { url?: unknown; width?: unknown; height?: unknown; } | undefined;
    if (typeof media?.url !== 'string' || !media.url || (typeof media.width === 'number' && media.width > 0 && typeof media.height === 'number' && media.height > 0)) return undefined;
    const imageUrl = media.url;
    const size = await new Promise<{ width: number; height: number; } | undefined>((resolve) => {
      const image = new Image();
      const timeout = window.setTimeout(() => resolve(undefined), 5000);
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        window.clearTimeout(timeout);
        resolve(image.naturalWidth > 0 && image.naturalHeight > 0
          ? { width: image.naturalWidth, height: image.naturalHeight } : undefined);
      };
      image.onerror = () => {
        window.clearTimeout(timeout);
        resolve(undefined);
      };
      image.src = imageUrl;
    });
    return size;
  }));
  return {
    ...snapshot,
    blocks: blocks.map((node, index) => {
      const size = sizes[index];
      if (!size) return node;
      return {
        ...node,
        data: {
          ...node.data,
          media_list: [{ ...node.data!.media_list[0], ...size }, ...node.data!.media_list.slice(1)],
        },
      };
    }),
  };
}

/** 每次展开生成独立快照；只有群组自身保留来源 IP，成员可在解组后作为普通节点继续使用。 */
export function buildIpGroupSnapshot(
  item: MyIpItem,
  position: { x: number; y: number; },
  id = nanoid(),
  options: { includeMaterials?: boolean; labels?: IpGroupRoleLabels; } = {},
): WorkflowNodeJSON {
  const value = getMyIpCreationValue(item);
  const ipAssetType: IpGroupAssetType = item.assetType === 'official' ? 'official' : 'personal';
  const materials = options.includeMaterials === false ? [] : value.materials ?? [];
  const labels = options.labels ?? DEFAULT_IP_GROUP_ROLE_LABELS;
  const snapshot: WorkflowNodeJSON = {
    id,
    type: 'group',
    meta: { position },
    data: {
      groupMode: 'ip',
      ip_id: item.ipId,
      ipId: item.ipId,
      ipAssetType,
      title: item.name,
      avatarUrl: item.avatarUrl || value.main_visual?.url,

    },
    blocks: [buildIpGroupMediaNode(value.main_visual, 'main', item.name, nanoid(), 0, labels), buildIpGroupMediaNode(value.turnaround, 'turnaround', item.name, nanoid(), 0, labels),
      ...(value.voice?.url?.trim() ? [buildIpGroupMediaNode(value.voice, 'voice', item.name, nanoid(), 0, labels)] : []),
      ...materials.map((media, index) => buildIpGroupMediaNode(media, 'material', item.name, nanoid(), index, labels))].map((node) => ({
      ...node, data: { ...node.data, ipAssetType },
    })),
  };
  // 缺少音频时不保留空槽位置；按 id 写回，避免布局结果顺序与 blocks 索引错位。
  const positions = layoutIpGroupChildren(snapshot.blocks!.map((node) => ({
    id: node.id, role: node.data?.ipGroupRole as IpGroupRole, ...getIpGroupChildLayout(node.data?.ipGroupRole as IpGroupRole),
  })));
  const positionById = new Map(positions.map((childPosition) => [childPosition.id, childPosition]));
  return {
    ...snapshot,
    blocks: snapshot.blocks!.map((node) => {
      const childPosition = positionById.get(node.id);
      if (!childPosition) return node;
      return {
        ...node,
        meta: { ...node.meta, position: { x: childPosition.x, y: childPosition.y } },
      };
    }),
  };
}
